import { useMemo } from "react";

/**
 * Custom hook to generate lookup objects for MaterialTable columns
 * @param {Array} data - The table data array
 * @param {Array|Object} columns - Array of column field names OR object with column configs
 *   - Array format: ['column1', 'column2']
 *   - Object format: { column1: { limit: 100, customSort: fn }, column2: {} }
 * @param {Object} options - Global options for all lookups (applied to all columns)
 * @returns {Object} Object with lookup objects for each specified column
 */
export const useColumnLookups = (data, columns = [], options = {}) => {
  return useMemo(() => {
    // Normalize columns to always be an array of strings
    const normalizedColumns = Array.isArray(columns)
      ? columns
      : Object.keys(columns);

    if (!data || !Array.isArray(data) || data.length === 0) {
      // Return empty lookup objects if no data
      return normalizedColumns.reduce((acc, column) => {
        acc[column] = {};
        return acc;
      }, {});
    }

    return normalizedColumns.reduce((acc, column) => {
      // Get column-specific options if provided
      const columnOptions =
        typeof columns === "object" && !Array.isArray(columns)
          ? columns[column] || {}
          : {};

      const mergedOptions = { ...options, ...columnOptions };

      // Get unique values for this column
      const uniqueValues = [
        ...new Set(
          data
            .map((row) => row[column])
            .filter(
              (value) => value !== null && value !== undefined && value !== ""
            )
        ),
      ].sort();

      // Apply custom sorting if provided
      const sortedValues = mergedOptions.customSort
        ? uniqueValues.sort(mergedOptions.customSort)
        : uniqueValues;

      // Apply limit if provided
      const limitedValues = mergedOptions.limit
        ? sortedValues.slice(0, mergedOptions.limit)
        : sortedValues;

      // Create lookup object where key and value are the same
      acc[column] = limitedValues.reduce((lookup, value) => {
        lookup[value] = mergedOptions.displayTransform
          ? mergedOptions.displayTransform(value)
          : value;
        return lookup;
      }, {});

      return acc;
    }, {});
  }, [data, columns, options]);
};
